import React from 'react';

// Content for the per-service landing pages. Each entry drives a route
// (/services/<slug>), its meta tags, and its Service + FAQPage schema.
const SERVICES = [
  {
    slug: 'process-automation',
    name: 'Process Automation',
    metaTitle: 'Business Process Automation Services | Rapid Rune',
    metaDescription:
      'We map your manual workflows and rebuild them as automated systems connected to the tools you already use. Most projects go live within weeks.',
    heroTitle: (
      <>
        Workflows that
        <br />
        <em>run themselves.</em>
      </>
    ),
    intro:
      'Your team loses hours every week to copy-paste work: moving data between systems, filing tickets, chasing approvals, updating spreadsheets. We map those workflows and rebuild them as automated systems connected to the tools you already use.',
    includes: [
      {
        label: 'End-to-end workflow design',
        desc: 'We map the full process from trigger to completion, including the exceptions your team handles by hand today.',
      },
      {
        label: 'Custom API integrations',
        desc: 'When two systems have no native connector, we build the integration that links them.',
      },
      {
        label: 'ERP, CRM & helpdesk automation',
        desc: 'We connect the platforms that run your business so a record updated in one place updates in all of them.',
      },
      {
        label: 'Automated reporting',
        desc: 'Scheduled reports built from live data, so nobody exports spreadsheets on Friday afternoons.',
      },
      {
        label: 'User provisioning & deprovisioning',
        desc: 'New hires get accounts, licenses, and folder access the moment HR approves them. Departures lose access just as fast.',
      },
      {
        label: 'Real-time data sync',
        desc: 'When a record changes in one system, the change lands everywhere else within seconds.',
      },
    ],
    faqs: [
      {
        q: 'What business processes can you automate?',
        a: 'Anything with defined steps and digital inputs. Common projects include employee onboarding, invoice routing, report generation, CRM data entry, and helpdesk ticket triage. If your team repeats a task the same way each week, we can probably automate it.',
      },
      {
        q: 'Which automation tools do you use?',
        a: 'We build on the systems you already run, such as Microsoft 365, Google Workspace, and your CRM. For the automation layer we work with platforms like n8n and Power Automate, and we write custom API integrations where off-the-shelf connectors fall short.',
      },
      {
        q: 'How long does an automation project take?',
        a: 'Most projects go live within three to six weeks. We start with a workflow audit, agree on a fixed scope, then build and test everything before it touches production.',
      },
      {
        q: 'How much does process automation cost?',
        a: 'We quote a fixed price after scoping, so you know the full cost before we build anything. Ongoing monitoring and support are available on a retainer. We scope each project so the saved labor hours cover its cost within months.',
      },
      {
        q: 'What happens if an automation breaks?',
        a: 'We build error handling and alerts into every workflow, so a failure raises a flag instead of dropping records in silence. Support retainers cover fixes, and we document each system so your own team can maintain it.',
      },
      {
        q: 'Do we need to be located near you?',
        a: 'No. Rapid Rune works remotely with clients across the United States. Scoping calls, builds, and support all happen over video and screen share.',
      },
    ],
  },
  {
    slug: 'it-consulting-email-security',
    name: 'IT Consulting & Email Security',
    metaTitle: 'IT Consulting & Email Security Services | Rapid Rune',
    metaDescription:
      'DMARC, DKIM, and SPF configuration, security audits, zero-trust design, and a prioritized fix list in plain language. Remote, US-wide.',
    heroTitle: (
      <>
        IT foundations
        <br />
        that <em>hold up.</em>
      </>
    ),
    intro:
      'Spoofed email lands in inboxes because authentication is missing or misconfigured, and most businesses find out after the wire transfer clears. We configure DMARC, DKIM, and SPF, audit your security posture, and hand you a prioritized fix list in plain language.',
    includes: [
      {
        label: 'DMARC, DKIM & SPF',
        desc: 'Email authentication that blocks spoofing of your domain and improves your own deliverability.',
      },
      {
        label: 'Security audits',
        desc: 'A review of identity, email, admin access, MFA coverage, and device policy, with a ranked fix list.',
      },
      {
        label: 'Zero-trust architecture',
        desc: 'Access design where each request gets verified, so one stolen password stops being a company-wide breach.',
      },
      {
        label: 'Email threat protection',
        desc: 'Filtering and impersonation defenses configured in platforms such as Mimecast and Microsoft Defender.',
      },
      {
        label: 'Incident response',
        desc: 'When an account gets compromised, we contain it, trace what the attacker reached, and close the gap.',
      },
      {
        label: 'Infrastructure hardening',
        desc: 'Baseline configurations for servers, firewalls, and cloud services that remove the easy ways in.',
      },
    ],
    faqs: [
      {
        q: 'What is DMARC and why does my business need it?',
        a: 'DMARC tells receiving mail servers what to do with messages that fail SPF or DKIM checks. Without it, anyone can send email that appears to come from your domain, which is how most invoice fraud starts. A correct DMARC policy blocks that spoofing, and Google and Yahoo now require it for bulk senders.',
      },
      {
        q: 'Is my business too small to be a target?',
        a: 'Attackers target small businesses because their defenses tend to be thinner and their payments still clear. Automated phishing kits hit every domain they can find, so size offers no cover.',
      },
      {
        q: 'What does a security audit cover?',
        a: 'We review identity and access, email authentication, MFA coverage, admin privileges, and device policies. You get a ranked list of gaps, the risk each one carries, and what fixing it involves.',
      },
      {
        q: 'Can you improve our email deliverability?',
        a: 'Most deliverability problems trace back to misaligned SPF, DKIM, or DMARC records rather than to message content. We align the records, authenticate your sending services, and watch the reports until your mail lands.',
      },
      {
        q: 'Do we have to replace our current IT provider or MSP?',
        a: 'No. We work alongside internal IT teams and MSPs on scoped projects, then hand off documentation when the work is done.',
      },
    ],
  },
  {
    slug: 'microsoft-365-google-workspace',
    name: 'M365 & Google Workspace',
    metaTitle: 'Microsoft 365 & Google Workspace Consulting | Rapid Rune',
    metaDescription:
      'License audits, Entra ID and Conditional Access setup, SharePoint architecture, and tenant hardening. Pay less and use more of what you own.',
    heroTitle: (
      <>
        More from the tools
        <br />
        you <em>already pay for.</em>
      </>
    ),
    intro:
      'Most companies pay for licenses nobody uses and skip security features included in their plan. We audit your tenant, cut the license waste, and configure the protection and collaboration features you already own.',
    includes: [
      {
        label: 'License audit & cost optimization',
        desc: 'We match each user to the cheapest plan that covers their actual usage and remove seats nobody logs into.',
      },
      {
        label: 'Entra ID & Conditional Access',
        desc: 'Sign-in policies that weigh device, location, and risk, so stolen credentials stop working on their own.',
      },
      {
        label: 'SharePoint & Teams architecture',
        desc: 'A site and permission structure people can navigate, with sprawl archived and ownership assigned.',
      },
      {
        label: 'MFA & admin hardening',
        desc: 'Full MFA coverage, least-privilege admin roles, and break-glass accounts configured the way Microsoft intends.',
      },
      {
        label: 'Google Workspace configuration',
        desc: 'The same treatment for Google shops: access policies, sharing controls, and license cleanup.',
      },
      {
        label: 'Tenant security review',
        desc: 'A pass through your security defaults, legacy protocols, and external sharing settings, with each risk explained.',
      },
    ],
    faqs: [
      {
        q: 'Can you lower our Microsoft 365 costs?',
        a: 'In most tenants, yes. License audits turn up unused seats, duplicate subscriptions, and users on premium plans who need only email. We match each person to the plan that fits and cancel the rest.',
      },
      {
        q: 'What is Conditional Access?',
        a: 'Conditional Access is the Entra ID feature that decides whether a sign-in gets through based on who is signing in, from which device, and from where. Configured well, it blocks attackers who hold stolen passwords while your own team barely notices it running.',
      },
      {
        q: 'Do you work with Google Workspace or just Microsoft 365?',
        a: 'Both. The platforms differ in mechanics but share the same problems: loose sharing settings, missing MFA, and unused licenses. We work in whichever one runs your business.',
      },
      {
        q: 'Our SharePoint is a mess. Can you fix it?',
        a: 'Yes. We map what exists, archive what nobody touches, and rebuild the structure around how your teams work. We simplify permissions so people find their files without filing tickets to IT.',
      },
      {
        q: 'Will the changes disrupt our team?',
        a: 'We pilot changes with a small group first, tell people what will move before it moves, and schedule cutovers outside working hours where it matters.',
      },
    ],
  },
  {
    slug: 'marketing-automation',
    name: 'Marketing Automation',
    metaTitle: 'Marketing Automation Services | Rapid Rune',
    metaDescription:
      'Email sequences, CRM and lead scoring, nurture flows, and dashboards that keep leads moving without daily manual work.',
    heroTitle: (
      <>
        Marketing that
        <br />
        <em>keeps up.</em>
      </>
    ),
    intro:
      'Leads go cold when follow-up depends on someone remembering to send it. We build the email sequences, lead scoring, and dashboards that keep prospects moving through your pipeline while your team handles the conversations that matter.',
    includes: [
      {
        label: 'Email campaigns & nurture flows',
        desc: 'Sequences that respond to what a prospect does: opened, clicked, booked, or went quiet.',
      },
      {
        label: 'CRM & lead scoring',
        desc: 'Rules that rank prospects by fit and behavior, so sales calls the right people first.',
      },
      {
        label: 'Performance dashboards',
        desc: 'One view of campaign results, pipeline movement, and conversion, pulled from live data.',
      },
      {
        label: 'Content pipelines',
        desc: 'Scheduling and distribution workflows that publish on time across your channels.',
      },
      {
        label: 'Form & webhook integrations',
        desc: 'Website inquiries land in your CRM with source tracking intact, and the first response goes out within minutes.',
      },
      {
        label: 'List hygiene',
        desc: 'Bounce handling, deduplication, and re-permission flows that protect your sender reputation.',
      },
    ],
    faqs: [
      {
        q: 'Which marketing platforms do you work with?',
        a: 'We build around your existing stack: your CRM, your email platform, and your website forms. Where a native integration is missing, we connect the pieces through APIs and tools like n8n.',
      },
      {
        q: 'What is lead scoring and do I need it?',
        a: 'Lead scoring ranks prospects by how well they fit your customer profile and how they engage with your content. If your pipeline holds more leads than your team can call, scoring decides who gets called first. Small pipelines can skip it.',
      },
      {
        q: 'How is this different from hiring a marketing agency?',
        a: 'An agency plans campaigns and writes the content. We build the systems that deliver them: the sequences, the CRM logic, the tracking. We often work alongside an agency or an in-house marketer.',
      },
      {
        q: 'What results should we expect?',
        a: 'Faster first response to inquiries, fewer leads dropped between marketing and sales, and reporting you can read in one place. The dashboard we build shows the numbers, so results stay measurable.',
      },
    ],
  },
];

export default SERVICES;
